import { Dvr, FilterAlt, Refresh } from "@mui/icons-material";
import { Grid, IconButton } from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import { Pie, PieChart, ResponsiveContainer, Tooltip as RechartsTooltip, Legend, BarChart, CartesianGrid, XAxis, YAxis, Bar } from "recharts";
import ChartCard from "../../../../components/ChartCard";
import ContentTitle from "../../../../components/Content/ContentTitle";
import DataCard from "../../../../components/DataCard";
import Filters from "../../../../components/PartnerPms/Filters";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import { useUserContext } from "../../../../contexts/UserContext";
import DefaultConstants from "../../../../data/Constants";
import { GovernanceStatusData, OccupancyStatusData, RoomStatusData, RoomStatusEnum } from "../../../../data/Enums";
import StringHelper from "../../../../helpers/StringHelper";
import { INavigationLink } from "../../../../interfaces/INavigation";
import { usePartnerPmsContext } from "../../../../contexts/PartnerPmsContext";
import DateTimeHelper from "../../../../helpers/DateTimeHelper";
import { IReservation, IRoom } from "../../../../interfaces/IPartnerPms";
import { useEnvironmentContext } from "../../../../contexts/EnvironmentContext";
import Tooltip from "../../../../components/Tooltip";
import Reports, { ReportsAlias } from "../../../../data/Reports";
import IReport from "../../../../interfaces/IReport";
import { IRoomOverviewReportFilters } from "../Reports/RoomOverview/Filters";
import { useReportPreviewContext } from "../../../../contexts/ReportPreviewContext";
import useDrawer from "../../../../hooks/useDrawer";


const Dashboard = (linkProps: INavigationLink) => {
    const { translate } = useLanguageContext();
    const { environment } = useEnvironmentContext();
    const { userPermissions, enterprise } = useUserContext();
    const { 
        filteredRooms, 
        handleRefreshRooms, 
        expectedOccupancy, 
        handleRefreshExpectedOccupancy, 
        selectedBlock, 
        selectedFloor, 
        selectedGovernanceStatus, 
        selectedOccupancyStatus, 
        selectedRoomStatus, 
        selectedRoomType, 
        onlyUserRooms,
        expectedCheckInWithoutRoom,
        handleRefreshExpectedCheckInWithoutRoom
    } = usePartnerPmsContext();
    const { handleOpen } = useReportPreviewContext();

    const filterDrawer = useDrawer();

    useEffect(() => {
        handleRefresh();
        
        const milliseconds = environment.partnerPms.dataRefreshTimeInSeconds * 1000;
        const interval = setInterval(() => {
            handleRefresh();
        }, milliseconds);
            
        return () => clearInterval(interval);
        // eslint-disable-next-line
    }, []);
    

    const handleRefresh = useCallback(() => {
        handleRefreshRooms();
        handleRefreshExpectedCheckInWithoutRoom();
        handleRefreshExpectedOccupancy();
        // eslint-disable-next-line
    }, []);


    const handleOpenReportPreview = useCallback((key?: string, value?: any) => {
        if (!userPermissions.includes(ReportsAlias.Governance.RoomOverview)) {
            return;
        }

        const report = Reports.find(r => r.alias === ReportsAlias.Governance.RoomOverview) as IReport;
        let filters: IRoomOverviewReportFilters = {
            enterprise: enterprise,
            occupancyStatus: selectedOccupancyStatus,
            governanceStatus: selectedGovernanceStatus,
            roomStatus: selectedRoomStatus,
            block: selectedBlock,
            floor: selectedFloor,
            roomType: selectedRoomType,
            onlyUserRooms: onlyUserRooms,
        };

        if (key !== undefined && value !== undefined) {
            const keyFilter = key as keyof(IRoomOverviewReportFilters);

            filters = {
                ...filters,
                [keyFilter]: [
                    ...filters[keyFilter] as any[],
                    value
                ]
            };
        }

        handleOpen(report, filters);
        // eslint-disable-next-line
    }, [enterprise, selectedOccupancyStatus, selectedGovernanceStatus, selectedRoomStatus, selectedBlock, selectedFloor, selectedRoomType, onlyUserRooms]);


    const dashOccupancyStatusData = useMemo(() => {
        return Object.values(OccupancyStatusData).filter(s => s.partnerId).map(s => {
            const name = translate(s.name);
            const value = filteredRooms.filter(r => r.occupancyStatusId === s.partnerId).length;
            const percentage = filteredRooms.length === 0 ? 0 : value / filteredRooms.length * 100;

            return {
                chartData: {
                    name: name,
                    value: value,
                    fill: s.color
                },
                dataCard: <DataCard
                    variant='dashboard'
                    variantProps={{
                        dashboard: {
                            title: name,
                            value: value.toString(),
                            valueSulfix: translate('rooms'),
                            percentage: percentage
                        }
                    }}
                    color={s.color}
                    Icon={s.Icon}
                    key={s.key}
                    onClick={() => handleOpenReportPreview('occupancyStatus', s.key)}
                />
            };
        });

        // eslint-disable-next-line
    }, [OccupancyStatusData, filteredRooms, translate]);


    const dashRoomStatusData = useMemo(() => {
        return Object.values(RoomStatusData).map(s => {
            const name = translate(s.name);

            const roomKey = (s.partnerId ?? '') as keyof(IRoom);
            const reservationKey = (s.partnerId ?? '') as keyof(IReservation);
            
            let value = filteredRooms.filter(r => r[roomKey] === true || (r.checkInReservation && r.checkInReservation[reservationKey] === true)).length;
            if (s.key === RoomStatusEnum.ExpectedCheckIn) {
                value += expectedCheckInWithoutRoom.length ?? 0;
            }

            const percentage = filteredRooms.length === 0 ? 0 : value / filteredRooms.length * 100;

            return {
                key: s.key,
                chartBar: <Bar
                    dataKey={StringHelper.FirstLetterLowerCase(s.name)}
                    name={name}
                    fill={s.color}
                    key={s.key}
                />,
                chartData: {
                    name: name,
                    value: value,
                    fill: s.color
                },
                dataCard: <DataCard
                    variant='dashboard'
                    variantProps={{
                        dashboard: {
                            title: name,
                            value: value.toString(),
                            valueSulfix: translate(s.key === RoomStatusEnum.DoNotDisturb || s.key === RoomStatusEnum.RoomBlocking ? 'rooms' : 'reservations'),
                            percentage: percentage
                        }
                    }}
                    color={s.color}
                    Icon={s.Icon}
                    key={s.key}
                    onClick={() => handleOpenReportPreview('roomStatus', s.key)}
                />
            };
        });

        // eslint-disable-next-line
    }, [RoomStatusData, filteredRooms, translate, expectedCheckInWithoutRoom]);


    const dashGovernanceStatusData = useMemo(() => {
        return Object.values(GovernanceStatusData).filter(s => s.partnerId).map((s, index) => {
            const name = translate(s.name);
            const value = filteredRooms.filter(r => r.governanceStatusId === s.partnerId).length;
            const percentage = filteredRooms.length === 0 ? 0 : value / filteredRooms.length * 100;

            return {
                chartData: {
                    name: name,
                    value: value,
                    fill: s.color
                },
                dataCard: <DataCard
                    variant='dashboard'
                    variantProps={{
                        dashboard: {
                            title: name,
                            value: value.toString(),
                            valueSulfix: translate('rooms'),
                            percentage: percentage
                        }
                    }}
                    color={s.color}
                    Icon={s.Icon}
                    key={s.key}
                    onClick={() => handleOpenReportPreview('governanceStatus', s.key)}
                />
            };
        });
        
        // eslint-disable-next-line
    }, [GovernanceStatusData, filteredRooms, translate]);


    return (
        <>
            <Grid item xs={12} className='grid-header'>
                <Grid item className='grid-title'>
                    <ContentTitle 
                        name={translate(linkProps.name)} 
                        description={enterprise?.name}
                        applyTranslation={false}
                    />
                </Grid>

                <Grid item className='grid-tools'>
                    <Tooltip title={translate('filter')}>
                        <IconButton onClick={() => filterDrawer.handleOpen()}>
                            <FilterAlt />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={translate('refresh')}>
                        <IconButton onClick={handleRefresh}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>

            <Grid container 
                className='grid-content overflow-enabled justifycontent-flexstart aligncontent-flexstart' 
                rowSpacing={DefaultConstants.gridRowSpacing} 
                columnSpacing={DefaultConstants.gridColumnSpacing}
            >
                <Grid item lg={2.4} md={4} sm={12} xs={12}>
                    <DataCard
                        variant='dashboard' 
                        variantProps={{
                            dashboard: {
                                title: translate('total'),
                                value: filteredRooms.length.toString(),
                                valueSulfix: translate('rooms'),
                                percentage: 100
                            }
                        }}
                        Icon={Dvr}
                        className='color-primary'
                        onClick={() => handleOpenReportPreview()}
                    />
                </Grid>

                {
                    userPermissions.includes(`${linkProps.alias[0]}.Occupancy`) && dashOccupancyStatusData.map((status, index) => {
                        return <Grid item lg={2.4} md={4} sm={6} xs={12} key={index}>
                            {status.dataCard}
                        </Grid>;
                    })
                }

                {
                    userPermissions.includes(`${linkProps.alias[0]}.Governance`) && <>
                        <Grid item xs={12} />

                        {
                            dashGovernanceStatusData.map((status, index) => {
                                const gridMdSize = 12 / dashGovernanceStatusData.length;
                                return <Grid item lg={gridMdSize} md={4} sm={6} xs={12} key={index}>
                                    {status.dataCard}
                                </Grid>;
                            })
                        }
                    </>
                }

                {
                    userPermissions.includes(`${linkProps.alias[0]}.Occupancy`) && <>
                        <Grid item xs={12} />

                        {
                            dashRoomStatusData.map((status, index) => {
                                const gridMdSize = 12 / dashRoomStatusData.length;
                                return <Grid item lg={gridMdSize} md={4} sm={6} xs={12} key={index}>
                                    {status.dataCard}
                                </Grid>;
                            })
                        }
                    </>
                }

                <Grid item xs={12} />

                {
                    userPermissions.includes(`${linkProps.alias[0]}.Occupancy`) && (
                        <Grid item sm={6} xs={12}>
                            <ChartCard
                                title={translate('occupation')}
                                chart={
                                    <ResponsiveContainer>
                                        <PieChart>
                                            <Pie
                                                dataKey="value"
                                                isAnimationActive={true}
                                                paddingAngle={2}
                                                innerRadius={50}
                                                outerRadius={100}
                                                data={dashOccupancyStatusData.map(status => status.chartData)}
                                            />
                                            <RechartsTooltip />
                                            <Legend align='left' iconSize={12} height={40} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                }
                            />
                        </Grid>
                    )
                }

                {
                    userPermissions.includes(`${linkProps.alias[0]}.Governance`) && (
                        <Grid item sm={6} xs={12}>
                            <ChartCard
                                title={translate('governance')}
                                chart={
                                    <ResponsiveContainer>
                                        <PieChart>
                                            <Pie
                                                dataKey="value"
                                                isAnimationActive={true}
                                                paddingAngle={2}
                                                innerRadius={50}
                                                outerRadius={100}
                                                data={dashGovernanceStatusData.map(status => status.chartData)}
                                            />
                                            <RechartsTooltip />
                                            <Legend align='left'  iconSize={12} height={40} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                }
                            />
                        </Grid>
                    )
                }

                {
                    userPermissions.includes(`${linkProps.alias[0]}.ExpectedOccupancy`) && <>
                        <Grid item xs={12} />

                        <Grid item xs={12}>
                            <ChartCard
                                title={translate('expectedOccupancy')}
                                chart={
                                    <ResponsiveContainer>
                                        <BarChart
                                            margin={{
                                                top: 40,
                                                right: 20,
                                                left: 20,
                                            }}
                                            data={expectedOccupancy.map(occupancy => {
                                                return {
                                                    ...occupancy,
                                                    date: DateTimeHelper.Format(occupancy.date.toString())
                                                }
                                            })}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="date" />
                                            <YAxis />
                                            <RechartsTooltip />
                                            <Legend align="left" iconSize={12} height={40} />

                                            <Bar dataKey="initialOccupation" name={translate('initialOccupation')} fill="#01579b" />
                                            {
                                                dashRoomStatusData
                                                    .filter(s => s.key === RoomStatusEnum.ExpectedCheckIn || s.key === RoomStatusEnum.ExpectedCheckOut)
                                                    .map(s => s.chartBar)
                                            }
                                            <Bar dataKey="finalOccupation" name={translate('finalOccupation')} fill="#43a047" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                }
                            />
                        </Grid>
                    </>
                }
            </Grid>


            <Filters
                open={filterDrawer.open}
                handleClose={() => filterDrawer.handleClose()}
            />
        </>
    );
}

export default Dashboard;